import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { useEffect } from 'react';

// Soft-keyboard avoidance for forms / modals.  Listens for keyboard
// show/hide and applies a body padding offset so the active input
// isn't covered.  Capacitor's `Keyboard` plugin already does some of
// this on iOS via the `resizing: 'body'` config, but mid-modal inputs
// in MUI dialogs (which fix themselves to the viewport) still get
// hidden — this hook is the extra mile.
//
// Usage: call once near the app root.  Idempotent — multiple mounts
// are safe but wasteful.
export const useKeyboardAvoidance = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let showHandle;
    let hideHandle;
    const original = document.body.style.paddingBottom;
    Keyboard.addListener('keyboardWillShow', info => {
      // Push the body up by the keyboard height so fixed dialogs
      // anchored to bottom-of-viewport remain visible.
      document.body.style.paddingBottom = `${info.keyboardHeight}px`;
      // Scroll the focused element into view if MUI hasn't already.
      const el = document.activeElement;
      if (el && el.scrollIntoView) {
        // Tiny defer — let layout settle from the padding swap.
        setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
      }
    }).then(h => {
      showHandle = h;
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.paddingBottom = original;
    }).then(h => {
      hideHandle = h;
    });
    return () => {
      if (showHandle) showHandle.remove();
      if (hideHandle) hideHandle.remove();
      document.body.style.paddingBottom = original;
    };
  }, []);
};
